/**
 * لقطة سلّة الزائر المجهول (بلا حساب) — تُحفظ في public.cart_snapshots
 * مربوطةً بـ`visitor_id` بدل `user_id`.
 *
 * لماذا منفصلة عن `cartSnapshot.functions.ts`؟
 *  - تلك محميّة بـ`requireSupabaseAuth` ولا تعمل للمجهول.
 *  - ٩٩٪ من زوّارنا مجهولون، وهم هدف حملة «سلّة بلا شراء».
 *
 * الأمان: zod صارم + الكتابة عبر supabaseAdmin داخل الـhandler فقط
 * (لا سياسة anon مفتوحة للكتابة)، وupsert على visitor_id يمنع الإغراق.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const itemSchema = z.object({
  handle: z.string().max(255).optional().nullable(),
  title: z.string().max(300).optional().nullable(),
  variantTitle: z.string().max(300).optional().nullable(),
  image: z.string().max(1000).optional().nullable(),
  price: z.number().min(0).optional().nullable(),
  quantity: z.number().int().min(0).max(999),
  isGiftSample: z.boolean().optional(),
  variantId: z.string().max(255).optional().nullable(),
  selectedOptions: z
    .array(z.object({ name: z.string().max(120), value: z.string().max(200) }))
    .max(10)
    .optional(),
});

const schema = z.object({
  visitorId: z.string().min(8).max(64),
  items: z.array(itemSchema).max(50),
  itemsCount: z.number().int().min(0).max(9999),
  cartValue: z.number().min(0),
  cartCurrency: z.string().max(5).optional().nullable(),
  market: z.string().max(10).optional().nullable(),
  locale: z.string().max(5).optional().nullable(),
  checkoutUrl: z.string().max(2000).optional().nullable(),
});

export const syncAnonCartSnapshot = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => schema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    if (data.items.length === 0) {
      await supabaseAdmin.from("cart_snapshots").delete().eq("visitor_id", data.visitorId);
      return { ok: true as const, cleared: true };
    }

    // الفهرس على visitor_id جزئيّ → لا يصلح arbiter لـ ON CONFLICT (42P10).
    const payload = {
      items: data.items,
      items_count: data.itemsCount,
      cart_value: data.cartValue,
      cart_currency: data.cartCurrency ?? null,
      market: data.market ?? null,
      locale: data.locale ?? null,
      checkout_url: data.checkoutUrl ?? null,
      updated_at: new Date().toISOString(),
    };

    const { data: updated, error: updateError } = await supabaseAdmin
      .from("cart_snapshots")
      .update(payload)
      .eq("visitor_id", data.visitorId)
      .select("visitor_id");

    let error = updateError;
    if (!error && (updated?.length ?? 0) === 0) {
      const res = await supabaseAdmin
        .from("cart_snapshots")
        .insert({ visitor_id: data.visitorId, user_id: null, ...payload });
      error = res.error;
    }

    if (error) {
      console.error("[syncAnonCartSnapshot]", error.message);
      return { ok: false as const, cleared: false };
    }
    return { ok: true as const, cleared: false };
  });
